import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import { fromUnixTime, } from '@/resources/helpers';
import { LS_KEY_TOKEN, LS_KEY_REFRESH_TOKEN } from '@/resources/helpers';

interface RegisterUserForm {
  id?: number | null;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  password?: string | null;
  created_at?: string | null
  addresses?: any
  default_shipping?: number | null
}

interface UpdateUserData {
  firstname: string | null;
  lastname: string | null;
  phone: string | null;
  birthday: string | null;
  street: String[] | [];
  number: string | null;
  city: string | null;
  postcode: string | null;
  country: string | null;
}

interface ChangePasswordForm {
  newPassword: string | null;
  newPasswordConfirmed?: string | null;
  currentPassword?: string | null;
}

interface OrderItem {
  name: string;
}
interface Order {
  number: string;
  items: OrderItem[];
}


interface OrderEventItem {
  itemId: Number;
  orderNumber: string;
  productType: string;
  cancelled: Boolean;
  themeUuid: string;
  hostUuid: string;
  eventStatus: string;
  name: string;
  price: Number;
  sku: string;
  qty: Number;
  eventTimestamp: Number;
  eventDuration: Number;
  venueUuid: string;
  image: string
}

interface OrderEvents {
  items: OrderEventItem[];
  total_count: Number
}
interface OrderItem {
  itemId: Number
  productType: String
  cancelled: Boolean
  name: string
  price: Number
  sku: Number
  qty: Number
  image: String
  eventTimestamp: Number
  giftcards: any
}

interface Coupon {
  type: String
  total: number
  coupon_code: string
  promotion_code: string
}

export const useUserStore = defineStore('user', () => {
  const { $logger } = useNuxtApp();
  const user: Ref<RegisterUserForm | null> = ref(null);
  const userLoading = ref<boolean>(true);
  const emailConfirmationLoading = ref<boolean>(false);
  const orderEventProduct = ref([]);
  const orderEventProductFutureEvents = ref([]);
  const orderEventProductPastEvents = ref([]);
  const orders = ref<Order[]>([]);
  const ordersLoading = ref<boolean>(false);
  const order = ref<Order[]>([])
  const ordersTotalCount = ref<number>(0);
  const loginLoading = ref<boolean>(false);
  const registerLoading = ref<boolean>(false);
  const resetPasswordLoading = ref<boolean>(false);
  const orderEventItem = ref<OrderEventItem>(null)
  const orderItems = ref<OrderItem[]>(null)
  const orderDate = ref<string>(null)
  const orderNumber = ref<string>(null)
  let mergedDiscounts = {
    type: 'discount',
    total: 0,
    coupon_code: [],
    promotion_code: []
  };
  let mergedGiftcards = {
    type: 'giftcard',
    total: 0,
    coupon_code: [],
    promotion_code: []
  };
  const mergedCoupons = ref<Coupon[]>([])
  const orderEvents = ref<OrderEvents[]>([]);
  const orderEventsLoading = ref<boolean>(false);
  const totalEventCount = ref(0)
  const isLoggedOut = ref(false)

  function logout() {
    isLoggedOut.value = true
    user.value = null;
    localStorage.removeItem(LS_KEY_TOKEN);
    localStorage.removeItem(LS_KEY_REFRESH_TOKEN);
  }

  async function loadUser(tryRefresh = true) {
    const token = localStorage.getItem(LS_KEY_TOKEN)
    if (!token) return logout()

    try {
      userLoading.value = true;
      const response = await $fetch('/api/user/profile', {
        query: {
          token,
        }
      }).catch((error) => {
        throw new Error(error);
      });

      if (response?.code === 401 && tryRefresh) {
        await refreshUserToken();

      } else if (response?.code === 200) {
        user.value = response.result;

      }
    } catch (error) {
      $logger.error(error);
      throw new Error('Failed to load user');
    } finally {
      userLoading.value = false;
    }
  }

  async function refreshUserToken() {
    try {
      const refreshToken = localStorage.getItem(LS_KEY_REFRESH_TOKEN);

      if (refreshToken) {
        const refreshResponse = await $fetch('/api/user/refresh-login-token', {
          method: 'POST',
          body: JSON.stringify({
            refreshToken,
          })
        }).catch((error) => {
          throw new Error(error);
        });

        if (refreshResponse?.code === 200) {
          localStorage.setItem(LS_KEY_TOKEN, refreshResponse.result);
          await loadUser(false).catch((error) => {
            throw new Error(error);
          });
        }
      }
    } catch (error) {
      $logger.error(error);
    }
  }

  async function loginUser(username: string, password: string, rememberMe: boolean = false) {
    try {
      loginLoading.value = true;
      const response = await $fetch('/api/user/login', {
        method: 'POST',
        body: JSON.stringify({
          username,
          password
        })
      }).catch((error) => {
        throw new Error(error);
      });
      if (response?.code === 200) {
        localStorage.setItem(LS_KEY_TOKEN, response.result);

        if (rememberMe) {
          localStorage.setItem(LS_KEY_REFRESH_TOKEN, response.meta.refreshToken);
        }
      }
      return response;
    } catch (error) {
      $logger.error(error);
    } finally {
      loginLoading.value = false;
    }
  }

  async function registerUser(userForm: RegisterUserForm) {
    try {
      registerLoading.value = true;

      const response = await $fetch('/api/user/register', {
        method: 'POST',
        body: JSON.stringify({
          customer: {
            firstname: userForm.firstname,
            lastname: userForm.lastname,
            email: userForm.email
          },
          password: userForm.password
        })
      }).catch((error) => {
        throw new Error(error);
      });

      if (response?.code === 200) {
        user.value = response.result;
      }

      return response;
    } catch (error) {
      $logger.error(error);
    } finally {
      registerLoading.value = false;
    }
  }

  async function triggerResetPassword(email: string) {

    try {
      resetPasswordLoading.value = true;
      const response = await $fetch('/api/user/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          email,
          redirect_host: window.location.origin
        })
      }).catch((error) => {
        throw new Error(error);
      });

      return response;
    } catch (error) {
      $logger.error(error);
    } finally {
      resetPasswordLoading.value = false;
    }
  }

  async function loadOrders(page: number = 1, perPage: number = 20, cancelledOrders: number = 0, giftCardsOnly: number = 0, eventsOnly: string = 'all') {
    const token = localStorage.getItem(LS_KEY_TOKEN)
    if (!token) return logout()


    try {
      ordersLoading.value = true;
      const response = await $fetch('/api/user/orders', {
        query: {
          token,
          page,
          perPage,
          cancelledOrders,
          giftCardsOnly,
          eventsOnly
        }
      }).catch((error) => {
        throw new Error(error);
      });

      if (response?.code === 200) {
        orders.value = response.result.items
        orderEventProduct.value = orders.value
      }
    } catch (error) {
      $logger.error(error);
    } finally {
      ordersLoading.value = false;
    }
  }

  // function logout() {
  //   user.value = null;
  //   localStorage.removeItem(LS_KEY_TOKEN);
  //   localStorage.removeItem(LS_KEY_REFRESH_TOKEN);
  // }

  async function updateUserData(updateUserDataForm: UpdateUserData) {
    const token = localStorage.getItem(LS_KEY_TOKEN)

    if (!user.value || !token) return

    try {
      const result = await $fetch('/api/user/profile', {
        method: 'POST',
        body: JSON.stringify({
          token,
          customer: updateUserDataForm
        })
      }).catch((error) => {
        throw new Error(error);
      });
      return result
    } catch (error) {
      $logger.error(error)
    }
  }

  async function changePassword(passwordChangeForm: ChangePasswordForm) {
    const currentPassword = passwordChangeForm.currentPassword;
    const newPassword = passwordChangeForm.newPassword;
    const token = localStorage.getItem(LS_KEY_TOKEN)
    if (!token) return logout()

    try {
      const response = await $fetch('/api/user/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword,
          newPassword,
          token
        })
      }).catch((error) => {
        throw new Error(error);
      });

      return response
    } catch (error) {
      $logger.error(error);
    }
  }

  async function updatePassword(passwordChangeForm: ChangePasswordForm) {
    const newPassword = passwordChangeForm.newPassword;
    const newPasswordConfirmed = passwordChangeForm.newPasswordConfirmed;

    //The URLSearchParams constructor interprets plus signs (+) as spaces
    // Therefore we replace + with '%2B'
    const rawQueryString = window.location.search.replace(/\+/g, '%2B');

    // Use the processed query string with URLSearchParams
    const params = new URLSearchParams(rawQueryString);

    const token = params.get('token') || '';
    const email = params.get('email') || '';

    try {
      const response = await $fetch('/api/user/update-password', {
        method: 'POST',
        body: JSON.stringify({
          newPassword,
          newPasswordConfirmed,
          token,
          email
        })
      }).catch((error) => {
        throw new Error(error);
      });

      return response
    } catch (error) {
      $logger.error(error);
    }
  }


  const isUserLoggedIn = computed(() => localStorage.getItem(LS_KEY_TOKEN) !== null)

  function mergeCoupons(mergedCoupon: any, coupon: Coupon) {
    mergedCoupon.total += coupon.total;
    mergedCoupon.coupon_code.push(coupon.coupon_code);
    mergedCoupon.promotion_code.push(coupon.promotion_code);
    mergedCoupons.value.push(mergedCoupon)
  }

  async function loadOrder(orderId: string) {
    ordersLoading.value = true
    const token = localStorage.getItem(LS_KEY_TOKEN)
    if (!token) return logout()

    try {

      const { code, result } = await $fetch(`/api/user/order`, {
        query: {
          token,
          orderId
        }
      }).catch((error) => {
        throw new Error(error);
      });
      if (code === 200) {
        order.value = result
        orderItems.value = result.items
        orderDate.value = result.created_at
        orderNumber.value = result.number

        // reset coupon values before mergeCoupons
        mergedDiscounts.coupon_code = []
        mergedDiscounts.promotion_code = []
        mergedDiscounts.total = 0

        mergedGiftcards.coupon_code = []
        mergedGiftcards.promotion_code = []
        mergedGiftcards.total = 0

        mergedCoupons.value = []

        order.value.coupons?.forEach((coupon: Coupon) => {
          if (coupon.type === 'discount') {
            mergeCoupons(mergedDiscounts, coupon);
          } else if (coupon.type === 'giftcard') {
            mergeCoupons(mergedGiftcards, coupon);
          }
        });
      }

    } catch (err) {
      console.error('error in fetch oder', err)
    } finally {
      ordersLoading.value = false

    }
  }

  async function getPdfs(pdfUrl: string) {
    const token = localStorage.getItem(LS_KEY_TOKEN)
    if (!token) return logout()


    try {

      const response = await $fetch(`/api/user/pdf`, {
        query: {
          token,
          pdfUrl,
        }
      });


      // Check if the response is successful
      if (!response) {
        throw new Error(`HTTP error! Status: ${response}`);
      }

      const base64Data = response.content;
      const byteCharacters = atob(base64Data); // Decode base64

      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: response.mimeType });
      // Create a URL for the Blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a download link
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = response.fileName; // Use the filename from JSON response

      // Append the link to the document, trigger a click, and then remove it
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up by revoking the blob URL
      window.URL.revokeObjectURL(blobUrl);

    } catch (err) {
      console.error('err in pdf download', err)
    }

  }

  async function loadOrderEvent(orderItemId: string) {
    orderEventsLoading.value = true
    const token = localStorage.getItem(LS_KEY_TOKEN)
    if (!token) return logout()


    try {
      const response = await $fetch(`/api/user/order-event`, {
        query: {
          token,
          orderItemId
        }
      }).catch((error) => {
        throw new Error(error);
      });
      if (response.code === 200) {
        orderEventItem.value = response.result
      }

    } catch (err) {
      console.error('error in fetch oder', err)
    } finally {
      orderEventsLoading.value = false
    }
  }


  async function loadOrderEvents(pageSize: string = '1', currentPage: string = '1', period: string = 'future', sort: string = 'asc') {
    orderEventsLoading.value = true
    const token = localStorage.getItem(LS_KEY_TOKEN)
    if (!token) return logout()


    try {
      const response = await $fetch('/api/user/order-events', {
        query: {
          token,
          pageSize,
          currentPage,
          period,
          sort
        }
      })


      if (response?.code === 200) {
        orderEventProductFutureEvents.value = []
        orderEventProductPastEvents.value = []

        response.result?.items?.forEach(event => {
          if (fromUnixTime(event.eventTimestamp) >= new Date()) {
            orderEventProductFutureEvents.value.push(event)
          } else {
            orderEventProductPastEvents.value.push(event)
          }

        })
        totalEventCount.value = response.result?.total_count
      }

    } catch (err) {
      console.error('error in loadOrderEvents', err)
    }
    finally {
      orderEventsLoading.value = false
    }

  }

  async function resendEmailConfirmation($email: string) {
    try {
      emailConfirmationLoading.value = true;
      const response = await $fetch('/api/user/resend-email-confirmation', {
        method: 'POST',
        body: JSON.stringify({
          email: $email
        })
      });
      return response;
    } catch (error) {
      $logger.error
    } finally {
      emailConfirmationLoading.value = false;
    }
  }

  async function sendEmailConfirmation($token: string) {

    try {

      const response = await $fetch('/api/user/send-email-confirmation', {
        method: 'POST',
        body: JSON.stringify({
          token: $token
        })
      });

      return response?.code
    } catch (e) {
      console.error('Error in send confirmation email', e)
    }

  }
  return {
    user,
    registerLoading,
    orders,
    ordersLoading,
    ordersTotalCount,
    loadOrders,
    registerUser,
    loadUser,
    userLoading,
    loginUser,
    loginLoading,
    resetPasswordLoading,
    triggerResetPassword,
    logout,
    updateUserData,
    changePassword,
    updatePassword,
    isUserLoggedIn,
    orderEventProduct,
    orderEventProductFutureEvents,
    orderEventProductPastEvents,
    loadOrder,
    order,
    getPdfs,
    loadOrderEvent,
    orderEventItem,
    orderItems,
    orderDate,
    orderNumber,
    mergedCoupons,
    loadOrderEvents,
    orderEvents,
    totalEventCount,
    orderEventsLoading,
    resendEmailConfirmation,
    emailConfirmationLoading,
    sendEmailConfirmation,
    isLoggedOut
  };

});
